import { Banner } from '@/components/ui/banner'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { IndicationStatus } from './quoteCardTypes'

const STATUS_COPY: Record<IndicationStatus, string> = {
  [IndicationStatus.WILL_REQUEST_INFO]:
    'Underwriters plan to ask for additional information to provide a final price. Cai will notify you when this happens.',
  [IndicationStatus.AWAITING_LOSS_RUNS]:
    'This carrier needs your claims history (loss runs) before it can price.',
  [IndicationStatus.AWAITING_RECENT_LOSS_RUNS]:
    'This carrier needs loss runs from the last 60 days before it can price. The ones on file are too old.',
  [IndicationStatus.AWAITING_IFTA]: 'This carrier needs your IFTA fuel tax filing before it can price.',
  [IndicationStatus.AWAITING_MVR]: 'This carrier needs your driver records (MVRs) before it can price.',
  [IndicationStatus.HAS_QUESTIONS]: 'Underwriters have further questions.',
  [IndicationStatus.NEEDS_ATTESTATION]: 'This carrier requires acceptance of terms.',
  [IndicationStatus.REVIEWING_SUBMITTED_INFO]:
    'Underwriters are reviewing your information. Cai will notify you when final pricing is provided.',
}

// Purely informational — no icon, since nothing is being asked of the user yet.
const NO_ICON_STATUSES: ReadonlySet<IndicationStatus> = new Set([IndicationStatus.WILL_REQUEST_INFO])

/** Statuses whose way out is a file the customer supplies. */
const DOCUMENT_STATUSES: ReadonlySet<IndicationStatus> = new Set([
  IndicationStatus.AWAITING_LOSS_RUNS,
  IndicationStatus.AWAITING_RECENT_LOSS_RUNS,
  IndicationStatus.AWAITING_IFTA,
  IndicationStatus.AWAITING_MVR,
])

interface PricingStatusBannerProps {
  status: IndicationStatus
  onProvideInfo: () => void
  onReview?: () => void
  /** Opens the document upload. Without it the banner is informational only. */
  onUpload?: () => void
  className?: string
  /**
   * Self-contained rounded banner (for placement inside the card body) instead of the
   * default flush card-footer style. Used for action-required statuses that are lifted
   * above the coverage area so they aren't missed on mobile.
   */
  inline?: boolean
}

/**
 * Underwriting-status messaging shown while a quote carries indication (not-yet-final)
 * pricing. Default is a footer to the Quote Card — flush with its edges, top border only;
 * pass `inline` for a self-contained banner placed within the card body.
 */
export function PricingStatusBanner({
  status,
  onProvideInfo,
  onReview,
  onUpload,
  className,
  inline = false,
}: PricingStatusBannerProps) {
  const action = renderAction(status, onProvideInfo, onReview, onUpload)

  return (
    <Banner
      variant="warning"
      small
      icon={NO_ICON_STATUSES.has(status) ? null : undefined}
      action={action}
      className={cn('text-xs', !inline && 'rounded-none border-x-0 border-b-0', className)}
    >
      {STATUS_COPY[status]}
    </Banner>
  )
}

function renderAction(
  status: IndicationStatus,
  onProvideInfo: () => void,
  onReview?: () => void,
  onUpload?: () => void
) {
  if (status === IndicationStatus.HAS_QUESTIONS) {
    return (
      <Button size="sm" onClick={onProvideInfo}>
        Provide info
      </Button>
    )
  }
  // No handler means nothing can come of the click (e.g. the read-only shared page).
  if (status === IndicationStatus.NEEDS_ATTESTATION && onReview) {
    return (
      <Button size="sm" onClick={onReview}>
        Review
      </Button>
    )
  }
  if (!DOCUMENT_STATUSES.has(status) || !onUpload) return undefined
  return (
    <Button size="sm" onClick={onUpload}>
      Upload
    </Button>
  )
}
